/**
 * People READ + WRITE-RESPONSE wire types — the people index grid, the
 * by-code detail read, and the write endpoints' responses
 * (`apps/api/src/routes/people/people.ts`, mounted at `/api/v1/people`).
 *
 * The index is a plain RLS-scoped read: the Hono handler pages the
 * `person_index` view (`security_invoker = true`,
 * `20260924072529_person_index_view.sql`) past PostgREST's 1,000-row cap with
 * a keyset cursor (fetchAllKeyset, keys `created_at` then `id`) through the
 * per-request user client, so org RLS authorizes — on `people` AND on every
 * table the view reads (`business_contacts`, `businesses`, `person_skills`,
 * `skills`, `person_disciplines`, `disciplines`, `person_locations`,
 * `gis_locations`).
 *
 * No query params: the endpoint returns the COMPLETE org index; search, the
 * filter document (`peopleFilters.ts`) and sorting run client-side.
 *
 * TS interfaces rather than zod (precedent `projectsReads.ts`): the API builds
 * these, the web unwraps them, nothing validates them at runtime.
 */
/** The count keys of {@link PersonArchiveOnlyReasons}, in display order. */
export const PERSON_ARCHIVE_ONLY_COUNT_KEYS = [
    "ap_bills",
    "expenses",
    "ar_invoices",
    "ledger_transactions",
    "time_entries",
    "timesheets",
    "consent_records",
    "documents",
];
/** True when any reason forbids a delete — the DELETE route's 409 test and the form's Archive-vs-Delete switch. */
export function isPersonArchiveOnly(reasons) {
    return reasons.erased || PERSON_ARCHIVE_ONLY_COUNT_KEYS.some((key) => reasons[key] > 0);
}
// ---------------------------------------------------------------------------
// Coded error envelopes (the web reads `err.envelope?.code`)
// ---------------------------------------------------------------------------
export const PERSON_ERROR_CODES = {
    /** 409 — the person was GDPR-erased; no further edits. */
    erased: "person_erased",
    /** 409 — DELETE refused; archive instead (`reasons` says why). */
    archiveOnly: "person_archive_only",
    /**
     * 409 — a connection type is used by connections: DELETE refused (`count`
     * says how many), or a PATCH tried to change `is_directional` / clear
     * `reverse_name`. Deactivate instead.
     */
    connectionTypeInUse: "connection_type_in_use",
    /** 409 — another type in the same scope already has this name (case-insensitive). */
    connectionTypeNameTaken: "connection_type_name_taken",
    /** 404 — PATCH …/attachments/:attachmentId found no attachment the caller may change. */
    attachmentNotFound: "not_found",
    /** 409 — the document was retained on GDPR erasure; its visibility is locked. */
    attachmentRetained: "person_attachment_retained",
};
//# sourceMappingURL=peopleReads.js.map